import type { HoverRoofEstimate } from './hoverServices';
import {
    useOneButtonStore,
    type HoverPendingGenerateQuote,
    type PriceQuoteOverrides,
    type SelectedHoverEstimate,
} from '../../store/oneButtonStore';

/**
 * Re-apply Hover Convert square counts + $/sq price overrides onto a RetrievePriceQuote payload.
 * Prevents async RPQ refreshes from wiping Convert (flash of $34,500 → $0 with 0 sq's).
 */
export function mergeHoverEstimateIntoPriceQuote(
    priceQuote: Record<string, any> | null | undefined,
    estimate: SelectedHoverEstimate | null | undefined,
): Record<string, any> | null {
    if (priceQuote == null) return null;
    if (!estimate?.hoverEstimateID) return priceQuote;

    const normalPrice =
        Number(estimate.normalSqOverride) ||
        Number(estimate.normalSlopeFloorRate) ||
        0;
    const lowPrice =
        Number(estimate.lowSqOverride) ||
        Number(estimate.lowSlopeFloorRate) ||
        (normalPrice ? Number((normalPrice + 0.7).toFixed(2)) : 0);
    const roofSquareOverride = (estimate.lowSq || 0) + (estimate.normalSq || 0);
    const shingleColorSF =
        (estimate.shingleTypeName ? `${estimate.shingleTypeName} - ` : '') + (estimate.shingleType || '');
    const roofSlice = {
        LowSlope: estimate.lowSq || 0,
        NormalSlope: estimate.normalSq || 0,
        RoofSquare: roofSquareOverride,
        ShingleColor: estimate.shingleType || null,
        ShingleColorSF: shingleColorSF || null,
        ManufacturerName: estimate.shingleManufacturer || '',
        RoofProduct: estimate.shingleTypeName || '',
    };
    const prevRoofQuote = (priceQuote.RoofQuote || {}) as Record<string, any>;
    const interest = useOneButtonStore.getState().opportunityDetails?.OpportunityInterest;
    const isRoofOnly = !!interest?.RoofOnly;

    const next: Record<string, any> = {
        ...priceQuote,
        hoverEstimateID: estimate.hoverEstimateID,
        Hover_Opportunity_Estimate_Id: estimate.hoverEstimateID,
        LowSlope: estimate.lowSq || 0,
        NormalSlope: estimate.normalSq || 0,
        Low_Slope_Override: estimate.lowSq || 0,
        Normal_Slope_Override: estimate.normalSq || 0,
        RoofSquareOverride: roofSquareOverride,
        PricePerSqFeet: normalPrice || priceQuote.PricePerSqFeet || 0,
        normalSlopeFloorRate: estimate.normalSlopeFloorRate || 0,
        lowSlopeFloorRate: estimate.lowSlopeFloorRate || 0,
        // V1 SavePriceAndQuote keeps Roof populated even for RoofOnly (empty Roof → API NRE).
        Roof: { ...(priceQuote.Roof || {}), ...roofSlice },
        RoofQuote: {
            ...prevRoofQuote,
            Pricing_Override_Type: prevRoofQuote.Pricing_Override_Type || 'RoofSquares',
            Roof_Cost_Type: prevRoofQuote.Roof_Cost_Type || 'PricingOverride',
            Normal_Slope_Price_Override: normalPrice || null,
            Low_Slope_Price_Override: lowPrice || null,
        },
    };

    if (isRoofOnly) {
        next.RoofOnly = { ...(priceQuote.RoofOnly || {}), ...roofSlice };
    }

    return next;
}

export function mergeHoverEstimateIntoPriceQuoteOverrides(
    overrides: PriceQuoteOverrides | null | undefined,
    estimate: SelectedHoverEstimate | null | undefined,
): PriceQuoteOverrides | null {
    if (!estimate?.hoverEstimateID) return overrides ?? null;
    const roofSquareOverride = (estimate.lowSq || 0) + (estimate.normalSq || 0);
    const normalPrice =
        Number(estimate.normalSqOverride) ||
        Number(estimate.normalSlopeFloorRate) ||
        0;
    return {
        ...(overrides ?? {
            roofSquareOverride: null,
            normalSlopeOverride: null,
            lowSlopeOverride: null,
        }),
        roofSquareOverride,
        normalSlopeOverride: estimate.normalSq || 0,
        lowSlopeOverride: estimate.lowSq || 0,
        pricePerSqFt: normalPrice || overrides?.pricePerSqFt || null,
        selectedShingleId: estimate.shingleProductCode || overrides?.selectedShingleId || null,
        selectedShingleAlt: estimate.shingleType || overrides?.selectedShingleAlt || null,
    };
}

/**
 * Apply Hover Convert selection into OneButton store (Angular `openHoverJob` afterClose).
 *
 * V1 mapping notes:
 * - `LowSlope` / `NormalSlope` / `Low_Slope_Override` / `Normal_Slope_Override` on quoteDetails = **square counts**
 * - Estimate DTO `Normal_Slope_Override` / `Low_Slope_Override` map to client `normalSqOverride` / `lowSqOverride` = **$/sq price**
 *   → written to RoofQuote `Normal_Slope_Price_Override` / `Low_Slope_Price_Override` (Angular roof-cost form)
 */
export function applyHoverEstimateConvert(estimate: HoverRoofEstimate): void {
    const {
        opportunityDetails,
        priceQuoteOverrides,
        priceQuoteDetails,
        roofDetails,
        setSelectedHoverEstimate,
        setPriceQuoteOverrides,
        setPriceQuoteDetails,
        updateRoofDetails,
        setHoverPendingGenerateQuote,
    } = useOneButtonStore.getState();

    const normalPrice =
        Number(estimate.normalSqOverride) ||
        Number(estimate.normalSlopeFloorRate) ||
        0;
    const lowPrice =
        Number(estimate.lowSqOverride) ||
        Number(estimate.lowSlopeFloorRate) ||
        (normalPrice ? Number((normalPrice + 0.7).toFixed(2)) : 0);

    const selected: SelectedHoverEstimate = {
        hoverEstimateID: estimate.hoverEstimateID || '',
        lowSq: estimate.lowSq || 0,
        normalSq: estimate.normalSq || 0,
        /** $/sq — from API `Low_Slope_Override` on estimate DTO */
        lowSqOverride: lowPrice,
        /** $/sq — from API `Normal_Slope_Override` on estimate DTO */
        normalSqOverride: normalPrice,
        normalSlopeFloorRate: estimate.normalSlopeFloorRate || 0,
        lowSlopeFloorRate: estimate.lowSlopeFloorRate || 0,
        wastageSq: estimate.wastageSq || 0,
        pricing: estimate.pricing || 0,
        shingleType: estimate.shingleType || '',
        shingleTypeName: estimate.shingleTypeName || '',
        shingleManufacturer: estimate.shingleManufacturer || '',
        shingleProductCode: estimate.shingleProductCode || null,
        adders: estimate.adders || [],
    };
    setSelectedHoverEstimate(selected);

    const nextOverrides = mergeHoverEstimateIntoPriceQuoteOverrides(priceQuoteOverrides, selected);
    setPriceQuoteOverrides(nextOverrides);

    const nextPq = mergeHoverEstimateIntoPriceQuote(
        (priceQuoteDetails || {}) as Record<string, any>,
        selected,
    );

    if (estimate.adders && estimate.adders.length > 0 && nextPq) {
        nextPq.RoofAdders = estimate.adders.map((a, index) => ({
            ...a,
            id: index + 4,
            Commissonable: 'N',
            Description: a.description || '',
        }));
    }

    setPriceQuoteDetails(nextPq as any);

    if (estimate.adders && estimate.adders.length > 0) {
        const mappedAdders = estimate.adders.map((a, index) => ({
            ...a,
            id: index + 4,
        }));
        updateRoofDetails({
            ...(roofDetails || {}),
            RoofAdders: mappedAdders,
        });
    }

    /** Open Generate Quote for Roof (same as Quotes toolbar Add Quote → Roof). */
    const pending = buildHoverRoofGenerateQuoteSelection(opportunityDetails);
    if (pending) {
        setHoverPendingGenerateQuote(pending);
    }
}

function buildHoverRoofGenerateQuoteSelection(opportunityDetails: any): HoverPendingGenerateQuote | null {
    const activeProducts = (opportunityDetails?.OpportunityProduct || []).filter((p: any) => p.ActiveFlag);
    const roofProduct =
        activeProducts.find((p: any) => p.Product_Type_Name === 'Roof') ||
        activeProducts.find((p: any) => p.Product_Type_Name === 'Roof Only');
    if (!roofProduct) {
        const interest = opportunityDetails?.OpportunityInterest;
        if (!(interest?.Roof || interest?.RoofOnly)) return null;
        return {
            product: 'Roof',
            partner: 'Cash',
            methord: 'Cash Contract',
            price: null,
            ExcelOffer: null,
            payload: null,
            products: [
                {
                    product: 'Roof',
                    partner: 'Cash',
                    methord: 'Cash Contract',
                    price: null,
                    ExcelOffer: null,
                    payload: null,
                },
            ],
        };
    }

    let partner = roofProduct.Finance_Partner_Display_Name || roofProduct.Finance_Partner || 'Cash';
    if (partner === 'Good Leap') partner = 'GoodLeap';
    const methord = roofProduct.Finance_Method_Name || (String(partner).toLowerCase() === 'cash' ? 'Cash Contract' : null);

    return {
        product: 'Roof',
        partner,
        methord,
        price: null,
        ExcelOffer: null,
        payload: null,
        products: [
            {
                product: 'Roof',
                partner,
                methord,
                price: null,
                ExcelOffer: null,
                payload: null,
            },
        ],
    };
}
